import _ from "lodash";
import React from "react";
import { Tag } from "@toddle-edu/ds-web";
import { htmlToText } from "Utils";
import { QUESTION_TYPE_DETAILS } from "Worksheet/modules/WorksheetConstants";
import {
  getAllQuestionsFromSectionsV2,
  getQuestionTitle,
  isVisualWorksheetElement,
} from "Worksheet/modules/WorksheetUtils";

// This function returns a object of learning goals id mapped to question title, type and id
export const getLearningGoalsMappingFromSectionsV2 = _.memoize(
  ({ sections, learningGoalsFields, t }) => {
    const learningGoalsMapping = {};
    _.map(learningGoalsFields, (goal, key) => {
      learningGoalsMapping[key] = {};
    });
    const questions = getAllQuestionsFromSectionsV2(sections);

    let questionIndex = 0;
    _.forEach(questions, question => {
      const fields = _.get(question, "fields");
      const questionTypeUid = _.get(question, "questionType.uid");
      if (isVisualWorksheetElement(questionTypeUid)) return;
      questionIndex++;
      const questionTypeDetails = _.get(
        QUESTION_TYPE_DETAILS,
        questionTypeUid,
        {}
      );
      const { title: questionTitle } = getQuestionTitle({
        question,
        t,
      });
      const icon = (
        <span style={{ marginRight: 8 }}>
          <Tag
            dsVersion="2.0"
            _legacy_size="x-small"
            size="small"
            prefix={_.get(questionTypeDetails, "icon", null)}
          >
            {questionIndex}
          </Tag>
        </span>
      );
      _.map(fields, field => {
        const uid = _.get(field, "uid");
        if (uid in learningGoalsMapping) {
          const value = _.get(field, "value");
          const goalMap = _.get(learningGoalsMapping, uid);
          _.map(value, goalId => {
            if (_.isNil(goalMap[goalId])) {
              goalMap[goalId] = [];
            }
            goalMap[goalId].push({
              key: _.get(question, "id"),
              id: _.get(question, "id"),
              label: _.isEmpty(questionTitle)
                ? t("worksheet:untitled_question")
                : htmlToText(questionTitle),
              icon: icon,
            });
          });
        }
      });
    });
    return learningGoalsMapping;
  }
);

/**
 * Locates a question within nested sections and returns its resource ID, its index in a flat list of all questions,
 * and the ID of the section containing the question.
 * @param {Array} sections - A list of sections, each containing a collection of questions including potentially nested subquestions.
 * @param {string} activeQuestionId - The ID of the question to locate.
 * @returns {Object} An object containing the `questionResourceId`, `questionIndex`, and `sectionId` of the found question.
 */
export const getDetailsFromSectionsV2 = (sections = [], activeQuestionId) => {
  let questionIndex = 0;
  const questions = _.flatMapDeep(sections, section => {
    return section?.children;
  });
  _.some(questions, question => {
    questionIndex++;
    if (_.get(question, "item.id") === activeQuestionId) {
      return true;
    }
    return false; // Continue searching through other questions.
  });

  const sectionId = _.get(
    _.find(sections, section =>
      _.some(
        _.get(section, "children"),
        edge => _.get(edge, "item.id") === activeQuestionId
      )
    ),
    "item.id"
  );

  return { questionIndex, sectionId };
};
